"use client";

import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";

/** One line of a legend: a value the column can hold, what it means, and
 *  — for a colour-coded column — the swatch it wears (a `bg-*` class). */
export type LegendRow = { term: string; note: string; swatch?: string };

/** The frosted panel itself — rounded, hairline-bordered, a translucent fill
 *  over a blurred backdrop — for any floating card that is not this component
 *  (Timestamp positions its own through a portal). Padding, layout, and
 *  placement stay with the caller. */
export const TOOLTIP_PANEL_CLASS =
  "rounded-lg border border-line bg-bg-muted/90 shadow-lg backdrop-blur-xl";

/**
 * The house hover card: a frosted panel that appears above its trigger,
 * carrying the identifiers and one-liners a row has no room to print — a sku
 * beside a name, what an accessory IS beside its quantity.
 *
 * A styled panel rather than a native `title`, because `title` cannot be
 * styled at all: no padding, no radius, no glass, and a browser-chosen delay.
 * The frosting is the hero tape's treatment (a translucent fill over a blurred
 * backdrop), turned up enough that small type stays readable over whatever it
 * covers.
 *
 * Hover and keyboard focus show it, in CSS; where it sits is measured. The
 * panel is `position: fixed`, placed against its trigger each time it opens,
 * so no ancestor's overflow can cut it: a table is a scroll box, and a card
 * hanging off its last row, or a legend under the header of a table two rows
 * tall, ran past the box's edge and was clipped there. It keeps inside the
 * window (pulled left from the right edge, flipped to the other side of the
 * trigger when its own side has no room), and follows a scroll while open.
 * The panel is a SIBLING of the trigger, not a child, so a link's accessible
 * name stays the link's own text and the detail is read after it.
 *
 * `rows` makes it a legend: the title names the column and each row pairs a
 * value with what it means.
 *
 * The panel hangs off the trigger's own box. A trigger inside a cell that is
 * itself positioned (a sticky table header) can `anchor` to that container
 * instead, so the card starts at the cell's edge, clear of its padding and
 * its bottom rule.
 */
/** How far the panel keeps from its trigger, and from the window's edges. */
const GAP = 8;
const EDGE = 8;

export function Tooltip({
  children,
  title,
  note,
  rows,
  placement = "above",
  anchor = "trigger",
  className = "",
}: {
  /** The trigger — the text or control the panel describes. */
  children: ReactNode;
  /** The line that identifies: a sku, a code, an id. */
  title: string;
  /** An optional second line: what the thing IS. */
  note?: string | null;
  /** A legend under the title: each value the column can hold, and what it
   *  means. */
  rows?: readonly LegendRow[];
  /** Which side of the trigger the panel opens on. */
  placement?: "above" | "below";
  /** What the panel positions against: the trigger's box, or the nearest
   *  positioned ancestor. */
  anchor?: "trigger" | "container";
  /** Layout only (width, min-width) — never the panel's own styling. */
  className?: string;
}) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);

  const place = useCallback(() => {
    const wrap = wrapRef.current;
    const panel = panelRef.current;
    if (!wrap || !panel) return;
    // The trigger's own box, or the positioned cell it sits in (offsetParent
    // is that cell for an unpositioned wrapper, a <th> included).
    const box = (
      anchor === "container"
        ? ((wrap.offsetParent as HTMLElement | null) ?? wrap)
        : wrap
    ).getBoundingClientRect();
    // `fixed` is against the window unless an ancestor is a containing block
    // of its own (a dialog's scale, a frosted bar's backdrop-filter). Parked
    // at 0,0 the panel shows where that block starts, whichever it is.
    panel.style.left = "0px";
    panel.style.top = "0px";
    const parked = panel.getBoundingClientRect();
    const { width, height } = parked;
    const below = box.bottom + GAP;
    const above = box.top - GAP - height;
    const fitsBelow = below + height <= window.innerHeight - EDGE;
    const fitsAbove = above >= EDGE;
    const top =
      placement === "below"
        ? fitsBelow || !fitsAbove
          ? below
          : above
        : fitsAbove || !fitsBelow
          ? above
          : below;
    const left = Math.max(
      EDGE,
      Math.min(box.left, window.innerWidth - width - EDGE),
    );
    panel.style.left = `${left - parked.left}px`;
    panel.style.top = `${top - parked.top}px`;
  }, [anchor, placement]);

  // While it is open, a scroll (of the page or of the table it sits in) moves
  // the trigger out from under a fixed panel: follow it.
  const following = useRef(false);
  const follow = useCallback(
    (on: boolean) => {
      if (on === following.current) return;
      following.current = on;
      if (on) window.addEventListener("scroll", place, true);
      else window.removeEventListener("scroll", place, true);
    },
    [place],
  );
  useEffect(
    () => () => window.removeEventListener("scroll", place, true),
    [place],
  );

  return (
    <span
      ref={wrapRef}
      onPointerEnter={() => {
        place();
        follow(true);
      }}
      onPointerLeave={() => follow(false)}
      onFocus={() => {
        place();
        follow(true);
      }}
      onBlur={() => follow(false)}
      className={`group/tooltip inline-flex ${className}`.trim()}
    >
      {children}
      <span
        ref={panelRef}
        role="tooltip"
        className={`${TOOLTIP_PANEL_CLASS} pointer-events-none invisible fixed z-30 w-max text-left font-normal normal-case tracking-normal whitespace-normal opacity-0 transition-opacity group-hover/tooltip:visible group-hover/tooltip:opacity-100 group-focus-within/tooltip:visible group-focus-within/tooltip:opacity-100 ${
          rows ? "max-w-sm p-3" : "max-w-xs px-3 py-2"
        }`}
      >
        <span className="block font-mono text-xs text-fg">{title}</span>
        {note && (
          <span className="mt-1 block font-mono text-xs text-fg-muted">
            {note}
          </span>
        )}
        {rows && rows.length > 0 && (
          // A rounded hairline grid: the frame is the container's border, the
          // cells draw only the interior lines (a right rule on the term, a top
          // rule from the second row), so the corners stay clean.
          <span className="mt-2 grid grid-cols-[auto_1fr] overflow-hidden rounded-md border border-line font-mono text-xs">
            {rows.map((r, i) => (
              <Fragment key={r.term}>
                {/* The cell stretches to its row (its right rule runs the
                    row's height); the term sits at the row's middle, beside a
                    note that may run to several lines. */}
                <span
                  className={`flex items-center whitespace-nowrap border-r border-line px-2 py-1 text-fg ${i > 0 ? "border-t" : ""}`}
                >
                  {r.swatch && (
                    <span
                      aria-hidden
                      className={`mr-2 h-2 w-2 shrink-0 rounded-full ${r.swatch}`}
                    />
                  )}
                  {r.term}
                </span>
                <span
                  className={`border-line px-2 py-1 text-fg-muted ${i > 0 ? "border-t" : ""}`}
                >
                  {r.note}
                </span>
              </Fragment>
            ))}
          </span>
        )}
      </span>
    </span>
  );
}
